import { NavigationMenu } from "@base-ui/react/navigation-menu";
import type { SvgIconComponent } from "@mui/icons-material";
import SystemIcon from "@mui/icons-material/DeviceHubRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreRounded";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import GradingIcon from "@mui/icons-material/GradingRounded";
import InsightsIcon from "@mui/icons-material/InsightsRounded";
import LayersIcon from "@mui/icons-material/LayersRounded";
import MapIcon from "@mui/icons-material/MapRounded";
import MonitorIcon from "@mui/icons-material/MonitorHeartRounded";
import PolicyIcon from "@mui/icons-material/PolicyRounded";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemRounded";
import SecurityIcon from "@mui/icons-material/SecurityRounded";
import ShieldIcon from "@mui/icons-material/ShieldRounded";
import TableChartIcon from "@mui/icons-material/TableChartRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Typography } from "@mui/material";
import {
  Link,
  RegisteredRouter,
  useRouterState,
  ValidateLinkOptions,
} from "@tanstack/react-router";
import classes from "./HeaderNav.module.css";

export type NavItem<TRouter extends RegisteredRouter = RegisteredRouter, TOptions = unknown> = {
  Icon: SvgIconComponent;
  label: string;
  linkOptions: ValidateLinkOptions<TRouter, TOptions>;
};

export type NavSubGroup = {
  subLabel: string;
  linkOptions?: NavItem["linkOptions"];
  items: NavItem[];
};

export type NavGroup = {
  Icon: SvgIconComponent;
  label: string;
  items: (NavItem | NavSubGroup)[];
};

const isNavSubGroup = (item: NavItem | NavSubGroup): item is NavSubGroup => "subLabel" in item;

const systemGroup: NavGroup = {
  Icon: SystemIcon,
  label: "System",
  items: [
    {
      Icon: FactCheckRoundedIcon,
      label: "Kontrollpunkt",
      linkOptions: { to: "/kontrollpunkter" },
    },
    { Icon: LayersIcon, label: "Grunndata", linkOptions: { to: "/grunndata" } },
  ],
};

const analyseGroup: NavGroup = {
  Icon: InsightsIcon,
  label: "Analyse",
  items: [
    {
      Icon: TrendingUpIcon,
      label: "Avbruddsdata",
      linkOptions: { to: "/trend" },
    },
    { Icon: MonitorIcon, label: "Tilstand", linkOptions: { to: "/tilstand" } },
    {
      Icon: TimelineRoundedIcon,
      label: "Tilstandsutvikling",
      linkOptions: { to: "/tilstandsutvikling" },
    },
  ],
};

const risikoGroup: NavGroup = {
  Icon: ShieldIcon,
  label: "Risiko",
  items: [
    {
      Icon: ReportProblemOutlinedIcon,
      label: "Konsekvens",
      linkOptions: { to: "/kritikalitet" },
    },
    {
      Icon: SecurityIcon,
      label: "Barrierer",
      linkOptions: { to: "/barrierer" },
    },
  ],
};

const strategiGroup: NavGroup = {
  Icon: PolicyIcon,
  label: "Strategi",
  items: [
    {
      Icon: TableChartIcon,
      label: "Økonomisk sone",
      linkOptions: { to: "/strategi/economic-zone" },
    },
    {
      Icon: GradingIcon,
      label: "Vedlikeholdsnivåer",
      linkOptions: { to: "/strategi/inspection-point" },
    },
  ],
};

if (import.meta.env.WAI_MAINTENANCEHELPER_ENABLED || import.meta.env.DEV) {
  strategiGroup.items.push({
    Icon: FactCheckIcon,
    label: "Vedlikeholdsveileder",
    linkOptions: { to: "/strategi/vedlikeholdsveileder" },
  });
}

export const allNavLinks: (NavItem | NavGroup)[] = [
  systemGroup,
  analyseGroup,
  risikoGroup,
  strategiGroup,
  { Icon: MapIcon, label: "Kart", linkOptions: { to: "/kart/asset" } },
];

function isPathActive(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(to + "/");
}

function NavLink({ Icon, label, linkOptions }: NavItem) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isActive = isPathActive(pathname, linkOptions.to as string);
  return (
    <NavigationMenu.Link
      className={`${classes.navLink}${isActive ? " " + classes.navLinkActive : ""}`}
      closeOnClick
      render={<Link {...linkOptions} />}
    >
      <Icon fontSize="small" />
      <Typography variant="body2" component="span">
        {label}
      </Typography>
    </NavigationMenu.Link>
  );
}

function TopNavLink({ Icon, label, linkOptions }: NavItem) {
  return (
    <NavigationMenu.Link
      className={classes.topLink}
      closeOnClick
      render={<Link {...linkOptions} />}
    >
      <Icon fontSize="small" />
      {label}
    </NavigationMenu.Link>
  );
}

function NavGroupItem({ Icon, label, items }: NavGroup) {
  return (
    <NavigationMenu.Item>
      <NavigationMenu.Trigger className={classes.trigger}>
        <Icon fontSize="small" />
        {label}
        <ExpandMoreIcon className={classes.triggerChevron} fontSize="small" />
      </NavigationMenu.Trigger>
      <NavigationMenu.Content>
        <ul className={classes.contentList}>
          {items.map((item) =>
            isNavSubGroup(item) ? (
              <li key={item.subLabel}>
                {item.linkOptions ? (
                  <NavigationMenu.Link
                    className={classes.subGroupLabel}
                    closeOnClick
                    render={<Link {...item.linkOptions} />}
                  >
                    {item.subLabel}
                  </NavigationMenu.Link>
                ) : (
                  <Typography className={classes.subGroupLabel} component="p" variant="caption">
                    {item.subLabel}
                  </Typography>
                )}
                <ul className={classes.subGroupList}>
                  {item.items.map((subItem) => (
                    <li key={subItem.linkOptions.to}>
                      <NavLink {...subItem} />
                    </li>
                  ))}
                </ul>
              </li>
            ) : (
              <li key={item.linkOptions.to}>
                <NavLink {...item} />
              </li>
            ),
          )}
        </ul>
      </NavigationMenu.Content>
    </NavigationMenu.Item>
  );
}

export function HeaderNav() {
  return (
    <NavigationMenu.Root>
      <NavigationMenu.List className={classes.rootList}>
        <NavGroupItem {...systemGroup} />
        <NavGroupItem {...analyseGroup} />
        <NavGroupItem {...risikoGroup} />
        <NavGroupItem {...strategiGroup} />
        <NavigationMenu.Item>
          <TopNavLink Icon={MapIcon} label="Kart" linkOptions={{ to: "/kart/asset" }} />
        </NavigationMenu.Item>
      </NavigationMenu.List>

      <NavigationMenu.Portal>
        <NavigationMenu.Positioner className={classes.positioner} sideOffset={8}>
          <NavigationMenu.Popup className={classes.popup}>
            <NavigationMenu.Viewport className={classes.viewport} />
          </NavigationMenu.Popup>
        </NavigationMenu.Positioner>
      </NavigationMenu.Portal>
    </NavigationMenu.Root>
  );
}
